<script lang="ts" setup>
withDefaults(defineProps<{
    additionalClasses?: string;
    title: string;
    description?: string;
    hasHeader?: boolean;
    center?: boolean;
    width?: string;
    fullscreen?: boolean;
}>(), {
    additionalClasses: '',
    center: false,
    description: undefined,
    fullscreen: false,
    hasHeader: false,
    width: 'lg:max-w-[736px]',
});

const show = defineModel('show', { default: false });
</script>

<template>
    <DialogRoot v-model:open="show">
        <DialogTrigger v-if="$slots.trigger">
            <slot name="trigger" />
        </DialogTrigger>

        <DialogPortal>
            <Transition name="fade">
                <DialogOverlay
                    class="fixed inset-0 z-50 bg-gray-900 opacity-50"
                />
            </Transition>

            <Transition name="slide-up">
                <DialogContent
                    :class="[
                          'fixed inset-0 lg:inset-auto lg:left-[50%] lg:top-[50%]',
                          'z-[100]',
                          'flex w-full flex-col overflow-y-auto',
                          'bg-white',
                          'lg:translate-x-[-50%] lg:translate-y-[-50%]',
                          'focus:outline-none',
                            additionalClasses,
                            fullscreen ? 'h-full' : `${width} lg:max-h-[85vh] lg:rounded-lg`
                        ]"
                >
                    <div
                        v-if="hasHeader"
                        class="sticky inset-x-0 top-0 z-[110] flex h-[60px] w-full items-center justify-between border-b border-gray-100 bg-white">
                        <BaseHeading
                            variant="md-700"
                            :class="[center && 'text-center', 'px-dynamic']"
                        >
                            {{ title }}
                        </BaseHeading>
                        <div>
                            <DialogClose class="rounded p-dynamic" aria-label="Dialog Schließen">
                                <JamBaseIcon icon-name="close" size="large" />
                            </DialogClose>
                        </div>
                    </div>
                    <div class="px-dynamic py-6" :class="[ center && 'text-center']">
                        <div v-if="!hasHeader" class="fixed right-0 top-0 text-right">
                            <DialogClose class="rounded bg-white p-3" aria-label="Dialog Schließen">
                                <JamBaseIcon icon-name="close" size="large" />
                            </DialogClose>
                        </div>

                        <DialogTitle >
                            <template v-if="$slots.title">
                                <slot name="title" />
                            </template>
                            <BaseHeading
                                v-if="title"
                                variant="md-700"
                            >
                                {{ title }}
                            </BaseHeading>
                        </DialogTitle>
                        <DialogDescription  class="mt-4">
                            <template v-if="$slots.description">
                                <slot name="description" />
                            </template>
                            <BaseText v-if="description" color="text-gray-600">{{ description }}</BaseText>
                        </DialogDescription>
                        <slot />
                    </div>
                    <div
                        v-if="$slots.footer"
                        class="fixed bottom-0 flex h-[82px] w-full items-center justify-between border-t border-gray-100 bg-white px-dynamic py-4">
                        <slot name="footer" />
                    </div>
                </DialogContent>
            </Transition>
        </DialogPortal>
    </DialogRoot>
</template>

<style>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-up-enter-active {
    transition: transform 0.8s cubic-bezier(0.19, 1, 0.22, 1);
}

.slide-up-leave-active {
    transition: transform 0.2s cubic-bezier(0.55, 0.08, 0.68, 0.53);
}

.slide-up-leave-to,
.slide-up-enter-from {
    transform: translate(0%, 100%);

    @media only screen and (min-width: 1024px) {
        transform: translate(-50%, 100%);
    }
}
</style>
